import React from 'react';
import { useAppSelector } from '../../../../../../hooks/hooks';
import Box from '@mui/material/Box';
import styles from './info.module.scss';
import CalendarSelector from '../calendar-selector';
import moment from 'moment';
import { ViewCalendar } from '../../types';
import EditCalendarButton from '../edit-calendar-button';
import CreateNewButtonV2 from '../../../../../../components/shared/create-new-button-v2';
import Header from '../../../../../../components/shared/header';
import DeleteCalendarButton from '../delete-calendar-button';

interface CalendarInfoProps {
    onCreateClick: () => void;
    onEditClick: () => void;
    onDeleteClick: () => void;
    onSelectClick: (select?: ViewCalendar) => void;
    small?: boolean;
    canManage?: boolean;
}

const CalendarControl = (props: CalendarInfoProps) => {
    const {
        onCreateClick,
        onDeleteClick,
        onEditClick,
        onSelectClick
    } = props;
    const small = props.small;
    const canManage = props.canManage ?? true;
    const { workCalendars, workCalendarId, availableYears, currentYear } = useAppSelector(store => store.workCalendar);
    const availableWorkCalendars = React.useMemo(() => {
        return workCalendars
            .map((x) => availableYears.map((y) => ({ calendar: x.id, year: y.id, name: `${x.name} - ${y.name}` })))
            .flat(1)
            .map((x, i): ViewCalendar => ({ ...x, index: i }));
    }, [availableYears, workCalendars]);

    const resolveSelectedIndex = React.useCallback(() => {
        const currY = currentYear || moment().year();
        const current = availableWorkCalendars.find((x) => x.calendar === workCalendarId && x.year === currY);
        return current?.index ?? availableWorkCalendars[0]?.index ?? 0;
    }, [availableWorkCalendars, currentYear, workCalendarId]);

    const [selectedCalendar, setSelectedCalendar] = React.useState<number>(() => resolveSelectedIndex());

    React.useEffect(() => {
        setSelectedCalendar(resolveSelectedIndex());
    }, [resolveSelectedIndex]);

    const handleSelect = (value: number) => {
        setSelectedCalendar(value);
        onSelectClick(availableWorkCalendars[value]);
    }

    const selectorOptions = availableWorkCalendars.map(calendar => ({
        value: calendar.index.toString(),
        label: calendar.name,
    }));

    const handleSelectorChange = (value: string) => {
        const index = parseInt(value, 10);
        handleSelect(index);
    };

    const hasCalendarOptions = selectorOptions.length > 0;

    return(<Box className={styles.control}>
        <Box className={styles.item}>
            {hasCalendarOptions ? (
                <Header headerLocalizationId='year' textStyle={{fontSize: '22px', lineHeight: '30px'}}>
                    <CalendarSelector
                        options={selectorOptions}
                        value={selectedCalendar.toString()}
                        onSelect={handleSelectorChange}
                    />
                </Header>
            ) : (
                <Header headerLocalizationId='work_calendar' textStyle={{fontSize: '22px', lineHeight: '30px'}} />
            )}
        </Box>
        {
            !small && canManage && (
                <Box className={styles.item}>
                    {workCalendars.length > 1 && <DeleteCalendarButton onClick={onDeleteClick} />}
                    <EditCalendarButton onClick={onEditClick} />
                    <CreateNewButtonV2 onClick={onCreateClick} />
                </Box>
            )
        }
    </Box>)
}

export default function CalendarInfo(props: CalendarInfoProps) {
    return <CalendarControl {...props} />;
}
